/**
 * Toasts (CLAUDE.md 14.5).
 *
 * The application had no way to tell a user that something failed other than
 * rendering an error into the results pane, which meant a failed save or a rejected
 * report request simply vanished. This is that channel.
 *
 * Built on Radix Toast so the announcement is correct: `status` toasts are polite,
 * `error` toasts are assertive, both are keyboard-dismissible, and the viewport is a
 * proper landmark rather than a floating div.
 *
 * Toasts are for transient outcomes. Anything an engineer must act on -- a stale
 * position, a failing check -- belongs in the page, not in something that disappears.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'

export type ToastVariant = 'info' | 'success' | 'warning' | 'error'

interface ToastMessage {
  id: number
  title: string
  description?: string
  variant: ToastVariant
  /** An optional retry or undo. */
  action?: { label: string; onClick: () => void }
}

interface ToastContextValue {
  toast: (message: Omit<ToastMessage, 'id'>) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used within <ToastProvider>')
  return context
}

const VARIANTS: Record<ToastVariant, { icon: typeof Info; className: string }> = {
  info: { icon: Info, className: 'text-text' },
  success: { icon: CheckCircle2, className: 'text-[var(--status-pass)]' },
  warning: { icon: AlertTriangle, className: 'text-[var(--status-warn)]' },
  error: { icon: XCircle, className: 'text-[var(--status-fail)]' },
}

let nextId = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [messages, setMessages] = useState<ToastMessage[]>([])

  const toast = useCallback((message: Omit<ToastMessage, 'id'>) => {
    setMessages((current) => [...current, { ...message, id: nextId++ }])
  }, [])

  const dismiss = useCallback((id: number) => {
    setMessages((current) => current.filter((m) => m.id !== id))
  }, [])

  const value = useMemo(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {messages.map((message) => {
          const { icon: Icon, className } = VARIANTS[message.variant]
          return (
            <ToastPrimitive.Root
              key={message.id}
              // An error interrupts; a confirmation waits its turn.
              type={message.variant === 'error' ? 'foreground' : 'background'}
              duration={message.variant === 'error' ? 10_000 : 5_000}
              onOpenChange={(open) => {
                if (!open) dismiss(message.id)
              }}
              className={cn(
                'flex items-start gap-3 rounded-md border border-border bg-surface-raised p-3',
                'shadow-[var(--shadow-md)]',
                'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-2',
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
              )}
            >
              <Icon size={17} className={cn('mt-0.5 shrink-0', className)} aria-hidden />
              <div className="min-w-0 flex-1">
                <ToastPrimitive.Title className="text-sm font-medium">
                  {message.title}
                </ToastPrimitive.Title>
                {message.description && (
                  <ToastPrimitive.Description className="mt-0.5 text-sm text-text-muted">
                    {message.description}
                  </ToastPrimitive.Description>
                )}
              </div>
              {message.action && (
                <ToastPrimitive.Action
                  altText={message.action.label}
                  onClick={message.action.onClick}
                  className="shrink-0 rounded px-2 py-1 text-sm font-medium text-accent hover:bg-surface"
                >
                  {message.action.label}
                </ToastPrimitive.Action>
              )}
              <ToastPrimitive.Close
                aria-label={t('action.close')}
                className="shrink-0 rounded p-1 text-text-muted hover:text-text"
              >
                <X size={15} aria-hidden />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          )
        })}
        <ToastPrimitive.Viewport
          className={cn(
            'fixed bottom-0 end-0 z-[60] m-0 flex w-full max-w-sm list-none flex-col gap-2 p-4',
            // Clear of the mobile bottom tab bar and the home indicator (14.3).
            'pb-[calc(1rem+env(safe-area-inset-bottom)+3.5rem)] md:pb-4',
          )}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  )
}
